import { createFileRoute, Link } from "@tanstack/react-router";
import { useGetInspection } from "@/api/client-inspection";
import { PageStatus } from "@/components/page-status";
import { Button } from "@/components/ui/button";
import {
  DetailCardSkeleton,
  DetailHeaderSkeleton,
} from "@/components/ui/detail-skeleton";
import { requireMinRole } from "@/lib/route-guards";
import { PaperSheetView } from "@/sections/inspections/view/paper-sheet-view";

// Mirrors PaperSheetView's WorkflowChrome: non-sticky workflow header + the
// two-column upload / checklist layout.
function PaperSheetSkeleton() {
  return (
    <div className="flex min-h-full flex-col bg-background">
      <DetailHeaderSkeleton sticky={false} eyebrow subtitle trailingPills={1} />
      <div className="grid flex-1 gap-6 p-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex flex-col gap-4 min-w-0">
          <DetailCardSkeleton rows={4} />
          <DetailCardSkeleton rows={3} />
        </div>
        <div className="flex flex-col gap-4">
          <DetailCardSkeleton rows={5} />
        </div>
      </div>
    </div>
  );
}

function PaperSheetPage() {
  const { inspectionId } = Route.useParams();
  const { data, isLoading } = useGetInspection(inspectionId);

  if (isLoading) {
    return <PaperSheetSkeleton />;
  }

  if (!data) {
    return (
      <PageStatus
        variant="not-found"
        title="Inspection not found"
        description="This inspection may have been removed, or the reference in the link is incorrect. Check the reference and try again from the inspections list."
        actions={
          <Button
            nativeButton={false}
            render={<Link to="/dashboard/inspections" />}
          >
            Back to inspections
          </Button>
        }
      />
    );
  }

  return <PaperSheetView inspection={data} />;
}

export const Route = createFileRoute(
  "/dashboard/inspections/$inspectionId/paper-sheet",
)({
  component: PaperSheetPage,
  beforeLoad: ({ context }) => {
    requireMinRole(context.session, "inspector");
  },
  staticData: {
    title: "Paper Sheet",
    parent: { title: "Inspections", path: "/dashboard/inspections" },
  },
});
